import nacl from 'tweetnacl';
import bs58 from 'bs58';

/**
 * Phantom Mobile Wallet Encryption Utilities
 *
 * Implements the encryption/decryption flow required for Phantom mobile deeplinks.
 * Based on Phantom's official mobile deeplink protocol.
 *
 * Flow:
 * 1. Generate dapp keypair (TweetNaCl)
 * 2. Send public key in deeplink
 * 3. Phantom returns its public key + encrypted data
 * 4. Generate shared secret from both keys
 * 5. Decrypt response data
 * 6. Extract session and public_key
 */

const PHANTOM_KEYPAIR_KEY = 'phantom_mobile_dapp_keypair';
const PHANTOM_SESSION_KEY = 'phantom_mobile_session';
const PHANTOM_SHARED_SECRET_KEY = 'phantom_mobile_shared_secret';

/**
 * Generate a new encryption keypair for Phantom communication
 * Stores the keypair in localStorage for later use
 */
export const generatePhantomKeypair = (): nacl.BoxKeyPair => {
  const keypair = nacl.box.keyPair();

  // Store keypair in localStorage (base58 encoded)
  const keypairData = {
    publicKey: bs58.encode(keypair.publicKey),
    secretKey: bs58.encode(keypair.secretKey),
  };

  localStorage.setItem(PHANTOM_KEYPAIR_KEY, JSON.stringify(keypairData));

  return keypair;
};

/**
 * Get the stored dapp keypair from localStorage
 */
export const getStoredPhantomKeypair = (): nacl.BoxKeyPair | null => {
  const stored = localStorage.getItem(PHANTOM_KEYPAIR_KEY);
  if (!stored) return null;

  try {
    const keypairData = JSON.parse(stored);
    return {
      publicKey: bs58.decode(keypairData.publicKey),
      secretKey: bs58.decode(keypairData.secretKey),
    };
  } catch {
    return null;
  }
};

/**
 * Generate shared secret from dapp secret key and Phantom public key
 */
export const generateSharedSecret = (
  dappSecretKey: Uint8Array,
  phantomPublicKey: Uint8Array
): Uint8Array => {
  return nacl.box.before(phantomPublicKey, dappSecretKey);
};

/**
 * Decrypt data received from Phantom using shared secret
 */
export const decryptPhantomPayload = (
  encryptedData: string,
  nonce: string,
  sharedSecret: Uint8Array
): any => {
  try {
    const decrypted = nacl.box.open.after(
      bs58.decode(encryptedData),
      bs58.decode(nonce),
      sharedSecret
    );

    if (!decrypted) {
      throw new Error('Failed to decrypt payload');
    }

    // Convert decrypted bytes to string and parse JSON
    const decoder = new TextDecoder();
    const jsonString = decoder.decode(decrypted);

    return JSON.parse(jsonString);
  } catch (error) {
    console.error('[Phantom Encryption] Decryption failed:', error);
    throw error;
  }
};

/**
 * Encrypt payload for sending to Phantom (for signing requests)
 */
export const encryptPhantomPayload = (
  payload: any,
  sharedSecret: Uint8Array
): { nonce: string; encryptedPayload: string } => {
  try {
    // Convert payload to JSON string then to bytes
    const encoder = new TextEncoder();
    const payloadBytes = encoder.encode(JSON.stringify(payload));

    // Generate random nonce
    const nonce = nacl.randomBytes(24);

    // Encrypt using shared secret
    const encrypted = nacl.box.after(payloadBytes, nonce, sharedSecret);

    return {
      nonce: bs58.encode(nonce),
      encryptedPayload: bs58.encode(encrypted),
    };
  } catch (error) {
    console.error('[Phantom Encryption] Encryption failed:', error);
    throw error;
  }
};

/**
 * Store Phantom session after successful connection
 */
export const storePhantomSession = (session: string, sharedSecret: Uint8Array): void => {
  const sharedSecretBase58 = bs58.encode(sharedSecret);

  localStorage.setItem(PHANTOM_SESSION_KEY, session);
  localStorage.setItem(PHANTOM_SHARED_SECRET_KEY, sharedSecretBase58);
};

/**
 * Get stored Phantom session
 */
export const getPhantomSession = (): string | null => {
  const session = localStorage.getItem(PHANTOM_SESSION_KEY);
  if (session) {
  }
  return session;
};

/**
 * Get stored shared secret
 */
export const getPhantomSharedSecret = (): Uint8Array | null => {
  const stored = localStorage.getItem(PHANTOM_SHARED_SECRET_KEY);

  if (!stored) {
    return null;
  }

  try {
    const decoded = bs58.decode(stored);
    return decoded;
  } catch (error) {
    return null;
  }
};

/**
 * Clear all Phantom mobile session data
 */
export const clearPhantomSession = (): void => {
  localStorage.removeItem(PHANTOM_KEYPAIR_KEY);
  localStorage.removeItem(PHANTOM_SESSION_KEY);
  localStorage.removeItem(PHANTOM_SHARED_SECRET_KEY);
};

/**
 * Parse Phantom response from URL parameters
 * Returns parsed data ready for processing
 */
export interface PhantomConnectResponse {
  phantom_encryption_public_key: string;
  data: string;
  nonce: string;
  errorCode?: string;
  errorMessage?: string;
}

export const parsePhantomConnectResponse = (
  url: string = window.location.href
): PhantomConnectResponse | null => {
  try {
    const urlObj = new URL(url);
    const params = urlObj.searchParams;

    // Check for error response
    const errorCode = params.get('errorCode');
    const errorMessage = params.get('errorMessage');

    if (errorCode || errorMessage) {
      return {
        phantom_encryption_public_key: '',
        data: '',
        nonce: '',
        errorCode: errorCode || undefined,
        errorMessage: errorMessage || undefined,
      };
    }

    // Check for successful response
    const phantomPublicKey = params.get('phantom_encryption_public_key');
    const data = params.get('data');
    const nonce = params.get('nonce');

    if (!phantomPublicKey || !data || !nonce) {
      return null; // Not a Phantom response
    }

    return {
      phantom_encryption_public_key: phantomPublicKey,
      data,
      nonce,
    };
  } catch {
    return null;
  }
};

/**
 * Process Phantom connect response and extract session + public_key
 */
export interface PhantomConnectData {
  public_key: string;
  session: string;
}

export const processPhantomConnectResponse = (
  response: PhantomConnectResponse
): PhantomConnectData => {
  // Check for error
  if (response.errorCode || response.errorMessage) {
    throw new Error(response.errorMessage || 'Phantom connection failed');
  }

  // Get stored dapp keypair
  const dappKeypair = getStoredPhantomKeypair();
  if (!dappKeypair) {
    throw new Error('Dapp keypair not found. Connection flow corrupted.');
  }

  // Decode Phantom's public key
  const phantomPublicKey = bs58.decode(response.phantom_encryption_public_key);

  // Generate shared secret
  const sharedSecret = generateSharedSecret(dappKeypair.secretKey, phantomPublicKey);

  // Decrypt response data
  const decryptedData = decryptPhantomPayload(
    response.data,
    response.nonce,
    sharedSecret
  );

  // Store session and shared secret
  storePhantomSession(decryptedData.session, sharedSecret);

  return {
    public_key: decryptedData.public_key,
    session: decryptedData.session,
  };
};

/**
 * Parse and decrypt signed transaction response from Phantom
 */
export interface PhantomSignResponse {
  data: string;
  nonce: string;
  errorCode?: string;
  errorMessage?: string;
}

export const parsePhantomSignResponse = (
  url: string = window.location.href
): PhantomSignResponse | null => {
  try {
    const urlObj = new URL(url);
    const params = urlObj.searchParams;

    // Check for error
    const errorCode = params.get('errorCode');
    const errorMessage = params.get('errorMessage');

    if (errorCode || errorMessage) {
      return {
        data: '',
        nonce: '',
        errorCode: errorCode || undefined,
        errorMessage: errorMessage || undefined,
      };
    }

    // Check for successful sign response
    const data = params.get('data');
    const nonce = params.get('nonce');

    if (!data || !nonce) {
      return null; // Not a Phantom sign response
    }

    return { data, nonce };
  } catch {
    return null;
  }
};

/**
 * Process signed transaction response from Phantom
 */
export const processPhantomSignResponse = (
  response: PhantomSignResponse
): { transaction: string } => {
  // Check for error
  if (response.errorCode || response.errorMessage) {
    throw new Error(response.errorMessage || 'Transaction signing failed');
  }

  // Get stored shared secret
  const sharedSecret = getPhantomSharedSecret();
  if (!sharedSecret) {
    throw new Error('Shared secret not found. Please reconnect your wallet.');
  }

  // Decrypt signed transaction
  const decryptedData = decryptPhantomPayload(
    response.data,
    response.nonce,
    sharedSecret
  );

  return {
    transaction: decryptedData.transaction,
  };
};

/**
 * Process signed batch transactions response from Phantom
 * Used for signAllTransactions deeplink
 */
export const processPhantomSignAllTransactionsResponse = (
  response: PhantomSignResponse
): { transactions: string[] } => {
  // Check for error
  if (response.errorCode || response.errorMessage) {
    throw new Error(response.errorMessage || 'Batch transaction signing failed');
  }

  // Get stored shared secret
  const sharedSecret = getPhantomSharedSecret();
  if (!sharedSecret) {
    throw new Error('Shared secret not found. Please reconnect your wallet.');
  }

  // Decrypt signed transactions
  const decryptedData = decryptPhantomPayload(
    response.data,
    response.nonce,
    sharedSecret
  );

  if (!decryptedData.transactions || !Array.isArray(decryptedData.transactions)) {
    throw new Error('Invalid batch response format - transactions array not found');
  }

  // Return array of base58-encoded transactions
  return {
    transactions: decryptedData.transactions,
  };
};

/**
 * Parse and decrypt signed message response from Phantom
 * Used for message signing (e.g., wallet validation)
 */
export interface PhantomSignMessageResponse {
  data: string;
  nonce: string;
  errorCode?: string;
  errorMessage?: string;
}

export const parsePhantomSignMessageResponse = (
  url: string = window.location.href
): PhantomSignMessageResponse | null => {
  try {
    const urlObj = new URL(url);
    const params = urlObj.searchParams;

    // Check for error
    const errorCode = params.get('errorCode');
    const errorMessage = params.get('errorMessage');

    if (errorCode || errorMessage) {
      return {
        data: '',
        nonce: '',
        errorCode: errorCode || undefined,
        errorMessage: errorMessage || undefined,
      };
    }

    // Check for successful sign message response
    const data = params.get('data');
    const nonce = params.get('nonce');

    if (!data || !nonce) {
      return null; // Not a Phantom sign message response
    }

    return { data, nonce };
  } catch {
    return null;
  }
};

/**
 * Process signed message response from Phantom
 * Returns the signature bytes
 */
export const processPhantomSignMessageResponse = (
  response: PhantomSignMessageResponse
): { signature: Uint8Array } => {
  // Check for error
  if (response.errorCode || response.errorMessage) {
    throw new Error(response.errorMessage || 'Message signing failed');
  }

  // Get stored shared secret
  const sharedSecret = getPhantomSharedSecret();
  if (!sharedSecret) {
    throw new Error('Shared secret not found. Please reconnect your wallet.');
  }

  // Decrypt signed message response
  const decryptedData = decryptPhantomPayload(
    response.data,
    response.nonce,
    sharedSecret
  );

  // The response should contain a base58-encoded signature
  // Convert from base58 string to Uint8Array
  const signatureBytes = bs58.decode(decryptedData.signature);

  return {
    signature: signatureBytes,
  };
};
